'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { useWtGamesAppDetails } from '@/features/games-app-bridge/shared/hooks/use-games-app-details';
import { CustomDataPayload } from '@/features/gtm/internal/types/gtm-datalayer';
import { LocaleCode } from '@/i18n/locales';
import { useLocale } from 'next-intl';
import { getGeoLocationOnClient } from '@/shared/utils/geo-location/get-geolocation-on-client';
import { GeoLocation } from '@/shared/utils/geo-location/geo-location.type';
import { LOCALE_MAP } from '@/shared/utils/locale-map';
import { useAppStore } from '@/features/shared/app-store';
import { useCurrency } from '@/features/shared/hooks/use-currency';

export const GTMDatalayerProvider = () => {
  const { authenticatedUser, loading } = useUser();
  const currencyCode = useCurrency();
  const setCustomData = useAppStore((s) => s.gtmSetCustomData);
  const gamesAppDetails = useWtGamesAppDetails();
  const dp = 'wildgames';
  const locale = useLocale() as LocaleCode;
  const [country, setCountry] = useState<GeoLocation | null>(null);
  const [geoReady, setGeoReady] = useState(false);

  useEffect(() => {
    getGeoLocationOnClient()
      .then(setCountry)
      .finally(() => setGeoReady(true));
  }, []);

  //@Todo detect page type more reliable
  const payload: CustomDataPayload = useMemo(
    () => ({
      consoleVersion: gamesAppDetails?.gamesAppVersion ?? '',
      consoleChannel: gamesAppDetails?.gamesAppChannel ?? '',
      isWildMember: authenticatedUser?.isWildMember ?? false,
      currencyCode,
      ums: authenticatedUser?.umsGuid || undefined,
      pageType: '',
      page: {},
      source: gamesAppDetails?.isGamesApp ? 'GamesApp' : 'Web',
      userAgent: gamesAppDetails?.userAgent ?? '',
      userLanguage: LOCALE_MAP[locale],
      country: country?.countryISO,
      dp,
      brand: dp,
    }),
    [
      gamesAppDetails?.gamesAppVersion,
      gamesAppDetails?.gamesAppChannel,
      gamesAppDetails?.isGamesApp,
      gamesAppDetails?.userAgent,
      authenticatedUser?.isWildMember,
      authenticatedUser?.umsGuid,
      currencyCode,
      country,
      locale,
    ],
  );

  const prev = useRef<string>('');
  useEffect(() => {
    if (loading || !geoReady) return;

    const key = JSON.stringify(payload);
    if (prev.current === key) return;
    prev.current = key;
    window?.dataLayer?.push({
      event: 'custom-data',
      ...payload,
    });

    setCustomData(payload);
  }, [payload, authenticatedUser, gamesAppDetails, loading, geoReady, setCustomData]);

  return null;
};
